import { FolderOpen, Lock, Plus, TreeStructure, UsersThree, X } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useActionButton } from "~/actions/adapters/button";
import { newDocument } from "~/actions/definitions/documents";
import { useCollection, useDocuments, useLinkCollectionProject, useProjects } from "~/api/queries";
import { debateHref } from "~/api/researchTypes";
import { ABILITY } from "~/api/types";
import { CollectionAccessDialog } from "~/features/collections/CollectionAccessDialog";
import { CollectionInputCard } from "~/features/collections/CollectionInputCard";
import { PinnedRow } from "~/features/collections/PinnedRow";
import { DocumentList } from "~/features/documents/DocumentList";
import { StarButton } from "~/features/documents/StarButton";
import { EntityFlowsPanel } from "~/features/flows/EntityFlowsPanel";
import { MoveToProjectDialog } from "~/features/projects/MoveToProjectDialog";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { cn } from "~/foundation/utils/cn";
import { usePolicy } from "~/policies/usePolicy";

export type CollectionTab = "documents" | "drafts" | "archived";

const TABS: Array<{ id: CollectionTab; label: string }> = [
  { id: "documents", label: "Documents" },
  { id: "drafts", label: "Drafts" },
  { id: "archived", label: "Archived" },
];

const STATUS: Record<CollectionTab, "published" | "draft" | "archived"> = {
  documents: "published",
  drafts: "draft",
  archived: "archived",
};

export function CollectionScene({
  collectionId,
  tab,
}: {
  collectionId: string;
  tab: CollectionTab;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: collection, isLoading, isError } = useCollection(collectionId);
  const { data: documents, isLoading: documentsLoading } = useDocuments({
    collectionId,
    status: STATUS[tab],
  });
  const can = usePolicy(collection);
  const newDocumentButton = useActionButton(newDocument, collectionId);
  const { data: projects } = useProjects();
  const linkProject = useLinkCollectionProject();
  const [movingToProject, setMovingToProject] = useState(false);
  // W10 §8 — who can see this collection, and its archive/restore lifecycle.
  const [managingAccess, setManagingAccess] = useState(false);
  useDocumentTitle(collection?.name ?? t("Collection"));

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t("Loading")} />
      </div>
    );
  }
  if (isError || !collection) {
    return <Empty icon={FolderOpen} message={t("This collection could not be loaded")} />;
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <Text as="h1" variant="h2">
            {collection.emoji ? `${collection.emoji} ` : ""}
            {collection.name}
          </Text>
          {collection.description ? (
            <Text variant="secondary" className="mt-1">
              {collection.description}
            </Text>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* W10 §2 — a collection can be starred too; the star row carries `collectionId`. */}
          <StarButton collectionId={collectionId} />
          {can[ABILITY.collectionUpdate] ? (
            collection.projectId ? (
              <div className="flex items-center gap-1 rounded-sm border border-divider py-1 ps-2.5 pe-1 text-small text-text-secondary">
                <span className="truncate">
                  {projects?.find((p) => p.id === collection.projectId)?.name ?? t("Project")}
                </span>
                <IconButton
                  icon={X}
                  size="sm"
                  aria-label={t("Remove from project")}
                  onClick={() => linkProject.mutate({ collectionId, projectId: null })}
                />
              </div>
            ) : (
              <Button variant="neutral" onClick={() => setMovingToProject(true)}>
                {t("Move to project…")}
              </Button>
            )
          ) : null}
          {can[ABILITY.collectionUpdate] || can[ABILITY.collectionDelete] ? (
            <Button variant="neutral" icon={Lock} onClick={() => setManagingAccess(true)}>
              {t("Access")}
            </Button>
          ) : null}
          <Button
            variant="neutral"
            icon={TreeStructure}
            onClick={() =>
              navigate({ to: "/space/collection/$collectionId", params: { collectionId } })
            }
          >
            {t("Knowledge tree")}
          </Button>
          {/* Debate (2026-10-09): a council reads this collection's index as its evidence. */}
          <Button
            variant="neutral"
            icon={UsersThree}
            onClick={() =>
              navigate({
                href: debateHref(
                  { kind: "collection", id: collectionId },
                  t("What should we do about «{{title}}»?", { title: collection.name }),
                ),
              })
            }
          >
            {t("Debate this collection")}
          </Button>
          {can[ABILITY.collectionCreateDocument] && newDocumentButton.visible ? (
            <Button variant="primary" icon={Plus} onClick={newDocumentButton.onClick}>
              {t("New document")}
            </Button>
          ) : null}
        </div>
      </div>

      {movingToProject ? (
        <MoveToProjectDialog collection={collection} onClose={() => setMovingToProject(false)} />
      ) : null}

      {managingAccess ? (
        <CollectionAccessDialog collection={collection} onClose={() => setManagingAccess(false)} />
      ) : null}

      {collection.archivedAt ? (
        <div role="status" className="rounded-md border border-divider bg-bg-secondary p-3">
          <Text variant="small" className="text-text-secondary">
            {t("This collection is archived — it and its documents are hidden from the sidebar.")}
          </Text>
        </div>
      ) : null}

      {/* W10 §3 — what the workspace put at the top of this collection. */}
      <PinnedRow collectionId={collectionId} canManage={can[ABILITY.collectionUpdate] === true} />

      <CollectionInputCard
        collectionId={collectionId}
        canManage={can[ABILITY.collectionUpdate] === true}
      />

      <div
        role="tablist"
        aria-label={collection.name}
        className="flex gap-1 border-b border-divider"
      >
        {TABS.map((entry) => (
          <Link
            key={entry.id}
            to="/collections/$collectionId"
            params={{ collectionId }}
            search={{ tab: entry.id }}
            role="tab"
            aria-selected={tab === entry.id}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-body",
              tab === entry.id
                ? "border-accent font-medium text-text"
                : "border-transparent text-text-secondary hover:text-text",
            )}
          >
            {t(entry.label)}
          </Link>
        ))}
      </div>

      <DocumentList
        documents={documents}
        isLoading={documentsLoading}
        showCollection={false}
        emptyMessage={t("Nothing here yet")}
      />

      <EntityFlowsPanel kind="collection" entityId={collectionId} />
    </div>
  );
}
